import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ProgressSectionHeader } from "./ProgressSectionHeader";
import { SHELL_HEADER_OFFSET_PX } from "./ProgressFilters";

const SECTION_SCROLL_MARGIN = `calc(${SHELL_HEADER_OFFSET_PX}px + 20rem)`;

export const ProgressSection = ({
  id,
  title,
  eyebrow,
  description,
  headerAction = null,
  collapsible = true,
  open = true,
  onOpenChange,
  testId,
  sectionTestId,
  className = "",
  children,
}) => {
  const contentId = `${id}-content`;
  const triggerId = `${id}-trigger`;
  const toggleLabel = open ? `Collapse ${title} section` : `Expand ${title} section`;

  const header = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-5">
      <ProgressSectionHeader eyebrow={eyebrow} title={title} description={description} />
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {headerAction}
        {collapsible ? (
          <CollapsibleTrigger asChild>
            <button
              id={triggerId}
              type="button"
              aria-expanded={open}
              aria-controls={contentId}
              aria-label={toggleLabel}
              data-testid={`${sectionTestId || testId}-toggle`}
              className="btn-secondary-app inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.2em] uppercase"
            >
              <span>{open ? "Hide section" : "Show section"}</span>
              <ChevronDown
                className={cn(
                  "w-4 h-4 transition-transform",
                  open && "rotate-180",
                  "motion-reduce:transition-none"
                )}
                aria-hidden="true"
              />
            </button>
          </CollapsibleTrigger>
        ) : null}
      </div>
    </div>
  );

  const sectionClassName = cn(
    "app-card p-4 sm:p-6 mt-8 md:scroll-mt-[var(--progress-section-scroll-margin)]",
    className
  );

  if (!collapsible) {
    return (
      <section
        id={id}
        data-testid={testId}
        data-section-id={sectionTestId || id}
        className={sectionClassName}
        style={{ "--progress-section-scroll-margin": SECTION_SCROLL_MARGIN }}
      >
        {header}
        {children}
      </section>
    );
  }

  return (
    <section
      id={id}
      data-testid={testId}
      data-section-id={sectionTestId || id}
      className={sectionClassName}
      style={{ "--progress-section-scroll-margin": SECTION_SCROLL_MARGIN }}
    >
      <Collapsible open={open} onOpenChange={onOpenChange}>
        {header}
        <CollapsibleContent id={contentId} role="region" aria-labelledby={triggerId}>
          {children}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
};
